import axios, { AxiosResponse } from 'axios';
import authHeader from '@/services/auth-header';
import Page from '@/dto/taxation/Page';
import SublimeChargeDTO, { ChargeComponent } from '@/dto/subscription/SublimeChargeDTO';
import {
    SUBSCRIPTIONS_CHARGES_ENDPOINT, SUBSCRIPTIONS_PLANS_ENDPOINT, SUBSCRIPTIONS_ACTIVE_COMPONENT_ENDPOINT, SUBSCRIPTIONS_PAYPAL_ENDPOINT, SUBSCRIPTIONS_MY_ENDPOINT, SUBSCRIPTIONS_ENDPOINT,
    SUBSCRIPTIONS_USER_ENDPOINT, SUBSCRIPTIONS_PAYPAL_ADM_ENDPOINT
} from '@/constants/endpoints';
import ActivePlanComponentDTO from '@/dto/employment/ActivePlanComponentDTO';
import PolicyAcceptancePayload from '@/dto/auth/PolicyAcceptancePayload';

import SubscriptionPlanDTO, { Periodicity } from '@/dto/subscription/SubscriptionPlanDTO';
import UserSubscriptionDetailsDTO from '@/dto/subscription/UserSubscriptionDetailsDTO';
import AddProfilesQuoteDTO from '@/dto/subscription/quote/AddProfilesQuoteDTO';
import AgreementBriefDTO from '@/dto/subscription/AgreementBriefDTO';

export default new class SubscriptionService {
  public getAllSubscriptions(pageNumber: number, pageSize: number): Promise<AxiosResponse<Page<AgreementBriefDTO & { periodicity: Periodicity }>>> {
    const params: Record<string, any> = { pageNumber, pageSize };
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
    });
    return axios.get<Page<AgreementBriefDTO & { periodicity: Periodicity }>>(`${SUBSCRIPTIONS_ENDPOINT}?${query.toString()}`, { headers: authHeader() });
  }

  public createPayPalSubscriptionAdmin(planId: number, companies: number[], personId?: number | null, sendLinkToCustomer = true): Promise<AxiosResponse<string>> {
    return axios.post<string>(`${SUBSCRIPTIONS_PAYPAL_ADM_ENDPOINT}`,
      {
        paypalPlanIdInternal: planId,
        personId: personId ?? null,
        companies: companies ?? [],
        sendLinkToCustomer: sendLinkToCustomer
      },
      { headers: authHeader() }
    );
  }
  public getCharges(pageNumber: number, pageSize: number, companyId?: number, issuerAbbreviation?: string): Promise<AxiosResponse<Page<SublimeChargeDTO>>> {
    const params: Record<string, any> = {
      pageNumber,
      pageSize,
    };
    if (companyId !== undefined && companyId !== null) params.companyId = companyId;
    if (issuerAbbreviation) params.issuer = issuerAbbreviation;

    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
    });

    return axios.get<Page<SublimeChargeDTO>>(`${SUBSCRIPTIONS_CHARGES_ENDPOINT}?${query.toString()}`, { headers: authHeader() });
  }

  public getPlans(pageNumber: number, pageSize: number): Promise<AxiosResponse<Page<SubscriptionPlanDTO>>> {
    const params: Record<string, any> = { pageNumber, pageSize };
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
    });
    return axios.get<Page<SubscriptionPlanDTO>>(`${SUBSCRIPTIONS_PLANS_ENDPOINT}?${query.toString()}`, { headers: authHeader() });
  }

  public getActiveComponent(companyId: number, component: ChargeComponent): Promise<AxiosResponse<ActivePlanComponentDTO | null>> {
    const params: Record<string, any> = { companyId, component };
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
    });
    return axios.get<ActivePlanComponentDTO | null>(`${SUBSCRIPTIONS_ACTIVE_COMPONENT_ENDPOINT}?${query.toString()}`, { headers: authHeader() });
  }

  public createPayPalSubscription(planId: number, companies: number[], policies: PolicyAcceptancePayload, personId?: number | null): Promise<AxiosResponse<string>> {
    return axios.post<string>(`${SUBSCRIPTIONS_PAYPAL_ENDPOINT}`, {
      paypalPlanIdInternal: planId,
      personId: personId,
      companies: companies,
      policies: policies
    }, { headers: authHeader() });
  }

  public getMySubscriptions(): Promise<AxiosResponse<Array<UserSubscriptionDetailsDTO>>> {
    return axios.get<Array<UserSubscriptionDetailsDTO>>(`${SUBSCRIPTIONS_MY_ENDPOINT}`, { headers: authHeader() });
  }

  public getUserSubscriptions(userId: number): Promise<AxiosResponse<Array<UserSubscriptionDetailsDTO>>> {
    return axios.get<Array<UserSubscriptionDetailsDTO>>(`${SUBSCRIPTIONS_USER_ENDPOINT}/${userId}`, { headers: authHeader() });
  }

  public cancelSubscriptionRenewal(id: number): Promise<AxiosResponse<void>> {
    return axios.delete<void>(`${SUBSCRIPTIONS_ENDPOINT}/${id}`, { headers: authHeader() });
  }

  public addProfilesToPayPalSubscription(id: number, payload: { personId: number | null, companyIds: number[] }): Promise<AxiosResponse<string>> {
    return axios.post<string>(`${SUBSCRIPTIONS_ENDPOINT}/${id}/profiles`, payload, { headers: authHeader() });
  }

  public addProfilesToSubscriptionAdmin(
    subscriptionId: number,
    payload: { personId: number | null, companyIds: number[], sendLinkToCustomer?: boolean }
  ): Promise<AxiosResponse<string>> {
    return axios.post<string>(
      `${SUBSCRIPTIONS_ENDPOINT}/${subscriptionId}/profiles-adm`,
      payload,
      { headers: authHeader() }
    );
  }

  public getAddProfilesQuote(id: number, payload: { personId: number | null, companyIds: number[] }): Promise<AxiosResponse<AddProfilesQuoteDTO>> {
    return axios.post<AddProfilesQuoteDTO>(`${SUBSCRIPTIONS_ENDPOINT}/${id}/profiles/quote`, payload, { headers: authHeader() });
  }
}